import { useCallback, useEffect, useMemo, useState } from 'react';

import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { CurrentUser } from '@/react/lib/capabilities';

import { selectVisibleNav } from '../Sidebar/selectVisibleNav';
import type { SidebarUser } from '../Sidebar/SidebarUserProfile';
import type { NavFeature } from '../Sidebar/types';
import { MOBILE_DRAWER_CONTENT_ANIMATION, MOBILE_DRAWER_OVERLAY_ANIMATION } from './constants';
import { MobileSidebar } from './MobileSidebar';
import { MOBILE_SIDEBAR_OPEN_EVENT } from './mobileSidebarEvents';

const NO_FEATURES: readonly NavFeature[] = [];

export interface MobileSidebarDrawerProps {
  // Capability-gated nav, same contract as the desktop Sidebar island.
  currentUser?: CurrentUser;
  enabledFeatures?: readonly NavFeature[];
  user: SidebarUser;
  // Override for tests/storybook; defaults to the live browser location.
  pathname?: string;
}

function resolvePathname(pathnameProp?: string) {
  return pathnameProp ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
}

function useReactivePathname(pathnameProp?: string) {
  const controlled = pathnameProp !== undefined;
  const [autoPathname, setAutoPathname] = useState(() => resolvePathname());

  useEffect(() => {
    if (controlled) return;
    const update = () => setAutoPathname(window.location.pathname);
    update();
    document.addEventListener('turbo:load', update);
    window.addEventListener('popstate', update);
    return () => {
      document.removeEventListener('turbo:load', update);
      window.removeEventListener('popstate', update);
    };
  }, [controlled]);

  return controlled ? resolvePathname(pathnameProp) : autoPathname;
}

// App-shell drawer that wraps the MobileSidebar panel in a left Sheet. Opened
// via `openMobileSidebar()` so page-level headers (e.g. orders mobile header)
// can trigger nav without sharing React state with this turbo-permanent island.
export function MobileSidebarDrawer({
  currentUser,
  enabledFeatures = NO_FEATURES,
  user,
  pathname: pathnameProp,
}: MobileSidebarDrawerProps) {
  const [open, setOpen] = useState(false);
  const pathname = useReactivePathname(pathnameProp);
  const nav = useMemo(() => selectVisibleNav(currentUser, enabledFeatures), [currentUser, enabledFeatures]);

  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const openDrawer = () => setOpen(true);
    document.addEventListener(MOBILE_SIDEBAR_OPEN_EVENT, openDrawer);
    return () => document.removeEventListener(MOBILE_SIDEBAR_OPEN_EVENT, openDrawer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('turbo:before-visit', close);
    return () => document.removeEventListener('turbo:before-visit', close);
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="left"
        hideClose
        onOpenAutoFocus={(event) => event.preventDefault()}
        overlayClassName={MOBILE_DRAWER_OVERLAY_ANIMATION}
        className={cn(
          'w-full max-w-none border-0 bg-off-white p-0 sm:max-w-none',
          MOBILE_DRAWER_CONTENT_ANIMATION,
        )}
      >
        <SheetTitle className="sr-only">Navigation menu</SheetTitle>
        <SheetDescription className="sr-only">App navigation links and account shortcuts</SheetDescription>
        <MobileSidebar
          nav={nav}
          user={user}
          pathname={pathname}
          onClose={handleClose}
        />
      </SheetContent>
    </Sheet>
  );
}

export default MobileSidebarDrawer;